import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { createPortal } from 'react-dom'
import { useRealtime, useOnlineRecovery } from '@papan/realtime/client'
import { ForbiddenOverlay } from './ForbiddenOverlay.js'
import { FileStateBanner } from './FileStateBanner'
import { useSaveRefusalToast } from './useSaveRefusalToast.js'
import { Loader2, Search, X, ChevronUp, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown, Filter, Lock, Unlock, ArrowRightToLine, BarChart3, Copy } from 'lucide-react'
import { AgGridReact } from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
  type SelectionChangedEvent,
  type IHeaderParams,
} from 'ag-grid-community'
import { useFileIO } from '@papan/files/client/file-io'
import { dataGridTheme } from './data-grid-theme.js'
import { stripPointerSuffix } from '@papan/types'
import { useYjs } from '../client/useYjs.js'
import { useCommandRegistryOptional, useTheme, getThemeClass, formatShortcut } from '@papan/core-ui/ui'
import { errorText } from '@papan/api-client'

// Register AG Grid community modules
ModuleRegistry.registerModules([AllCommunityModule])

// Dark theme based on AG Grid's Quartz (shared with LargeCsvGrid)
const darkTheme = dataGridTheme

interface DataGridViewerProps {
  fileId: string
  fileName?: string
  /** Collab room name — enables CRDT live updates for text formats (CSV) */
  room?: string
  /** Called when dirty state changes (CRDT mode only) */
  onDirtyChange?: (dirty: boolean) => void
  /** Ref to expose save function (CRDT mode only) */
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  /** Ref to expose discard function (CRDT mode only) */
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  /** Called when connection state changes (CRDT mode only) */
  onConnectedChange?: (connected: boolean) => void
  /** Called when reload state changes — a reconnect forced a fresh Y.Doc and it
   *  hasn't resynced yet (CRDT mode only). Lifted so the wrapper toolbar shows
   *  the shared "Reconnecting…" status. */
  onReloadingChange?: (reloading: boolean) => void
  /** Called when the bounded reconnect gives up (CRDT mode only) — lifted so the
   *  toolbar shows an honest "Disconnected — reload" instead of a stuck spinner. */
  onGaveUpChange?: (gaveUp: boolean) => void
  /** Ref to expose current selection for chat panel context */
  selectionRef?: React.MutableRefObject<{ text: string; rowCount: number } | null>
  isActive?: boolean
  viewSync?: Record<string, Record<string, unknown>>
  viewSyncId?: string
  viewSyncTrigger?: number
}

interface SearchMatch {
  rowIdx: number
  colId: string
}

interface ColumnStats {
  column: string
  count: number
  distinct: number
  numericCount: number
  sum: number
  avg: number
  min: number
  max: number
}

function computeStats(values: unknown[], colName: string): ColumnStats | null {
  if (values.length === 0) return null
  const distinct = new Set(values.map(v => String(v ?? ''))).size
  const nums: number[] = []
  for (const v of values) {
    const n = Number(v)
    if (v !== '' && v != null && !isNaN(n)) nums.push(n)
  }
  if (nums.length === 0) {
    return { column: colName, count: values.length, distinct, numericCount: 0, sum: 0, avg: 0, min: 0, max: 0 }
  }
  const sum = nums.reduce((a, b) => a + b, 0)
  return {
    column: colName,
    count: values.length,
    distinct,
    numericCount: nums.length,
    sum,
    avg: sum / nums.length,
    min: Math.min(...nums),
    max: Math.max(...nums),
  }
}

function formatNum(n: number): string {
  if (Number.isInteger(n)) return n.toLocaleString()
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Custom header component: label selects column for stats, sort button sorts, filter button opens filter */
function CustomHeader(props: IHeaderParams & {
  onColumnSelect?: (colId: string) => void
  selectedColumn?: string | null
  onHeaderContextMenu?: (colId: string, x: number, y: number) => void
}) {
  const [sort, setSort] = useState<'asc' | 'desc' | null>(null)
  const [filterActive, setFilterActive] = useState(false)
  const [hovered, setHovered] = useState(false)
  const filterBtnRef = useRef<HTMLButtonElement>(null)
  const colId = props.column.getColId()
  const isSelected = props.selectedColumn === colId

  useEffect(() => {
    const onSort = () => setSort(props.column.getSort() ?? null)
    const onFilter = () => setFilterActive(props.column.isFilterActive())
    props.column.addEventListener('sortChanged', onSort)
    props.column.addEventListener('filterChanged', onFilter)
    onSort()
    onFilter()
    return () => {
      props.column.removeEventListener('sortChanged', onSort)
      props.column.removeEventListener('filterChanged', onFilter)
    }
  }, [props.column])

  const showSort = hovered || isSelected || sort != null
  const showFilter = hovered || isSelected || filterActive

  return (
    <div
      className="flex items-center w-full h-full gap-0.5 min-w-0"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onContextMenu={(e) => {
        e.preventDefault()
        props.onHeaderContextMenu?.(colId, e.clientX, e.clientY)
      }}
    >
      <button
        className="flex-1 min-w-0 truncate text-left hover:text-white transition-colors"
        onClick={() => props.onColumnSelect?.(colId)}
        title={`Select "${props.displayName}" for stats`}
      >
        {props.displayName}
      </button>
      {props.enableSorting && showSort && (
        <button
          className="shrink-0 p-0.5 rounded hover:bg-white/10 transition-colors"
          onClick={(e) => props.progressSort(e.shiftKey)}
          title="Sort"
        >
          {sort === 'asc' ? <ArrowUp size={12} /> :
           sort === 'desc' ? <ArrowDown size={12} /> :
           <ArrowUpDown size={12} className="opacity-40" />}
        </button>
      )}
      {showFilter && (
        <button
          ref={filterBtnRef}
          className={`shrink-0 p-0.5 rounded hover:bg-white/10 transition-colors ${filterActive ? 'text-blue-400' : 'opacity-40'}`}
          onClick={() => filterBtnRef.current && props.showFilter(filterBtnRef.current)}
          title="Filter"
        >
          <Filter size={12} />
        </button>
      )}
    </div>
  )
}

/** Stats metrics content — shared between inline and popup views */
function StatsMetrics({ stats }: { stats: ColumnStats }) {
  return (
    <>
      <span>Count: {stats.count.toLocaleString()}</span>
      <span>Distinct: {stats.distinct.toLocaleString()}</span>
      {stats.numericCount > 0 && (
        <>
          <span>Sum: {formatNum(stats.sum)}</span>
          <span>Avg: {formatNum(stats.avg)}</span>
          <span>Min: {formatNum(stats.min)}</span>
          <span>Max: {formatNum(stats.max)}</span>
        </>
      )}
    </>
  )
}

/** Status bar with overflow detection — collapses metrics into a popup button when space is tight */
function StatsBar({ stats, frozenColumns, onToggleFreeze, onMoveToBack, onDismiss }: {
  stats: ColumnStats
  frozenColumns: Set<string>
  onToggleFreeze: (col: string) => void
  onMoveToBack: (col: string) => void
  onDismiss: () => void
}) {
  const barRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const metricsBtnRef = useRef<HTMLButtonElement>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [showPopup, setShowPopup] = useState(false)
  const [popupLeft, setPopupLeft] = useState(0)
  const popupRef = useRef<HTMLDivElement>(null)
  const isFrozen = frozenColumns.has(stats.column)

  // Measure full content width via hidden element, compare to container width
  useEffect(() => {
    const bar = barRef.current
    const measure = measureRef.current
    if (!bar || !measure) return
    const check = () => {
      setCollapsed(measure.scrollWidth > bar.clientWidth)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(bar)
    return () => ro.disconnect()
  }, [stats])

  // Close popup on outside click
  useEffect(() => {
    if (!showPopup) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (popupRef.current && !popupRef.current.contains(target) && !metricsBtnRef.current?.contains(target)) {
        setShowPopup(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showPopup])

  const actionButtons = (
    <>
      <button
        onClick={() => onToggleFreeze(stats.column)}
        className="flex items-center gap-1 hover:themed-text transition-colors px-1.5 py-0.5 rounded hover:themed-bg-tertiary shrink-0"
        title={isFrozen ? 'Unfreeze column' : 'Freeze column'}
      >
        {isFrozen ? <Unlock size={12} /> : <Lock size={12} />}
        <span>{isFrozen ? 'Unfreeze' : 'Freeze'}</span>
      </button>
      <button
        onClick={() => onMoveToBack(stats.column)}
        className="flex items-center gap-1 hover:themed-text transition-colors px-1.5 py-0.5 rounded hover:themed-bg-tertiary shrink-0"
        title="Move column to end"
      >
        <ArrowRightToLine size={12} />
        <span>Move to back</span>
      </button>
      <button
        onClick={onDismiss}
        className="hover:themed-text p-0.5 shrink-0"
      >
        <X size={12} />
      </button>
    </>
  )

  return (
    <div className="relative">
      {/* Hidden measurement div — always renders full metrics to measure true width */}
      <div
        ref={measureRef}
        className="h-0 overflow-hidden flex items-center px-4 gap-3 text-xs pointer-events-none"
        aria-hidden
      >
        <span className="shrink-0">{stats.column}</span>
        <StatsMetrics stats={stats} />
        <div className="flex-1" />
        {actionButtons}
      </div>

      <div
        ref={barRef}
        className="h-8 border-t themed-border flex items-center px-4 gap-3 shrink-0 themed-bg-secondary text-xs themed-text-muted"
      >
        <span className="themed-text font-medium shrink-0">{stats.column}</span>

        {collapsed ? (
          <button
            ref={metricsBtnRef}
            onClick={() => {
              setShowPopup(p => !p)
              if (metricsBtnRef.current && barRef.current) {
                setPopupLeft(metricsBtnRef.current.offsetLeft - barRef.current.offsetLeft)
              }
            }}
            className="flex items-center gap-1 hover:themed-text transition-colors px-1.5 py-0.5 rounded hover:themed-bg-tertiary shrink-0"
          >
            <BarChart3 size={12} />
            <span>Metrics</span>
          </button>
        ) : (
          <div className="flex items-center gap-3 min-w-0">
            <StatsMetrics stats={stats} />
          </div>
        )}

        <div className="flex-1" />
        {actionButtons}
      </div>

      {/* Metrics popup */}
      {showPopup && (
        <div
          ref={popupRef}
          className="absolute bottom-full mb-1 border themed-border rounded-lg shadow-lg px-3 py-2 text-xs themed-text-muted flex flex-col gap-1.5 z-10"
          style={{ left: popupLeft, backgroundColor: '#141416' }}
        >
          <StatsMetrics stats={stats} />
        </div>
      )}
    </div>
  )
}

/** Parse CSV text into rows. Handles quoted fields with commas/newlines. */
export function parseCsv(text: string): { columns: string[]; rows: Record<string, string>[] } {
  const lines: string[][] = []
  let row: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else {
      if (ch === '"') {
        inQuotes = true
      } else if (ch === ',') {
        row.push(field)
        field = ''
      } else if (ch === '\n') {
        row.push(field)
        field = ''
        lines.push(row)
        row = []
      } else if (ch === '\r') {
        // skip
      } else {
        field += ch
      }
    }
  }
  if (field || row.length > 0) {
    row.push(field)
    lines.push(row)
  }

  if (lines.length === 0) return { columns: [], rows: [] }

  const columns = lines[0]
  const rows = lines.slice(1).map(line => {
    const record: Record<string, string> = {}
    columns.forEach((col, i) => {
      record[col] = line[i] ?? ''
    })
    return record
  })

  return { columns, rows }
}

/** Parse parquet file using hyparquet (lazy loaded) */
export async function parseParquet(buffer: ArrayBuffer): Promise<{ columns: string[]; rows: Record<string, unknown>[] }> {
  const { parquetRead, parquetMetadataAsync } = await import('hyparquet')
  const metadata = await parquetMetadataAsync(buffer)

  const columns = metadata.schema.slice(1).map(s => s.name)
  const rows: Record<string, unknown>[] = []

  await parquetRead({
    file: buffer,
    metadata,
    onComplete(data: unknown[][]) {
      for (const row of data) {
        const record: Record<string, unknown> = {}
        columns.forEach((col, i) => {
          record[col] = row[i]
        })
        rows.push(record)
      }
    },
  })

  return { columns, rows }
}

/** Build grid data from Yjs maps (reads the target sheet, or first sheet as fallback) */
function buildGridDataFromYjs(doc: import('yjs').Doc, targetSheetId?: string): { columns: string[]; rows: Record<string, unknown>[] } {
  const sheetsMap = doc.getMap('sheets')
  const wb = doc.getMap('workbook')
  const order = (wb.get('sheetOrder') as string[]) || []
  // Use target sheet if it exists in the doc, otherwise fall back to first sheet
  const sheetId = (targetSheetId && order.includes(targetSheetId)) ? targetSheetId : order[0]
  if (!sheetId) return { columns: [], rows: [] }

  const sheet = sheetsMap.get(sheetId) as import('yjs').Map<unknown> | undefined
  if (!sheet) return { columns: [], rows: [] }

  const meta = sheet.get('meta') as import('yjs').Map<unknown>
  const cells = sheet.get('cells') as import('yjs').Map<unknown>
  if (!meta || !cells) return { columns: [], rows: [] }

  const rowCount = (meta.get('rows') as number) || 0
  const colCount = (meta.get('cols') as number) || 0
  if (rowCount === 0 || colCount === 0) return { columns: [], rows: [] }

  // Row 0 = column headers (display interpretation — not a data model concept)
  const columns: string[] = []
  for (let c = 0; c < colCount; c++) {
    columns.push((cells.get(`0:${c}`) as string) || `Column ${c + 1}`)
  }

  // Data rows start from row 1 (row 0 is used as headers for display)
  const rows: Record<string, unknown>[] = []
  for (let r = 1; r < rowCount; r++) {
    const record: Record<string, unknown> = { __rowIdx: r - 1 }
    for (let c = 0; c < colCount; c++) {
      record[columns[c]] = (cells.get(`${r}:${c}`) as string) || ''
    }
    rows.push(record)
  }
  return { columns, rows }
}

/**
 * CRDT-backed data source — uses useYjs for live updates (CSV).
 * Returns the same shape that DataGridViewerInner expects.
 */
function useCrdtData(room: string, fileId?: string, active?: boolean) {
  const { doc, connected, synced, isDirty, save, discard, forbidden, fileState, reloading, gaveUp, saveError } = useYjs({ room, active })
  // Surface a REFUSED save. The destructure omitted `saveError`, so this viewer
  // could not have shown one even though the server was sending the reason.
  useSaveRefusalToast(saveError)
  const [data, setData] = useState<{ columns: string[]; rows: Record<string, unknown>[] }>({ columns: [], rows: [] })

  useEffect(() => {
    if (!synced) return

    const apply = () => setData(buildGridDataFromYjs(doc, fileId))
    apply()

    // Observe the target sheet's nested cells + meta maps
    const sheetsMap = doc.getMap('sheets')
    const wb = doc.getMap('workbook')
    const order = (wb.get('sheetOrder') as string[]) || []
    const targetId = (fileId && order.includes(fileId)) ? fileId : order[0]
    if (!targetId) return

    const sheet = sheetsMap.get(targetId) as import('yjs').Map<unknown> | undefined
    if (!sheet) return

    const cells = sheet.get('cells') as import('yjs').Map<unknown>
    const meta = sheet.get('meta') as import('yjs').Map<unknown>
    if (!cells || !meta) return

    cells.observe(apply)
    meta.observe(apply)
    return () => {
      cells.unobserve(apply)
      meta.unobserve(apply)
    }
  }, [doc, synced, fileId])

  // #823: once moved to the bin the socket is closed for good — keep showing the last rows, never the spinner
  return { ...data, loading: !synced && !fileState, error: null as string | null, connected, isDirty, save, discard, forbidden, fileState, reloading, gaveUp }
}

/**
 * HTTP-fetch data source — one-time load (Parquet, or CSV without room).
 */
function useFetchData(fileId: string, ext: string) {
  const [data, setData] = useState<{ columns: string[]; rows: Record<string, unknown>[] }>({ columns: [], rows: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshCounter, setRefreshCounter] = useState(0)
  // #1316 review round 2: a load whose answer a session end dropped is asked again through `useDroppedLoadReask` —
  // in the share view it waits until the moved share session lands, never asking as the ended one
  const { reask, begin } = useDroppedLoadReask()
  const { onFileEvent } = useRealtime()
  const recovery = useOnlineRecovery()
  const fileIO = useFileIO()

  // Re-fetch when file is updated (e.g., by AI agent)
  useEffect(() => {
    return onFileEvent((event) => {
      if (event.fileId === fileId && (event.type === 'file:updated' || event.type === 'file:restored')) {
        setRefreshCounter(c => c + 1)
      }
    })
  }, [fileId, onFileEvent])

  useEffect(() => {
    const asked = begin()   // the share session this load goes out as
    setLoading(true)
    setError(null)

    fileIO.loadContent(fileId)
      .then(async (blob) => {
        let columns: string[]
        let rows: Record<string, unknown>[]

        if (ext === 'parquet') {
          const buffer = await blob.arrayBuffer()
          const result = await parseParquet(buffer)
          columns = result.columns
          rows = result.rows
        } else {
          const text = await blob.text()
          const result = parseCsv(text)
          columns = result.columns
          rows = result.rows
        }

        rows.forEach((r, i) => { r.__rowIdx = i })
        setData({ columns, rows })
        setLoading(false)
      })
      .catch(err => {
        const text = errorText(err)
        // #1316: a dropped answer (its session ended) is asked again as the session now here — never an error
        if (text === null) { asked.dropped(); return }
        setError(text)
        setLoading(false)
      })
  }, [fileId, ext, refreshCounter, recovery, fileIO, reask, begin])

  return { ...data, loading, error }
}

/** CRDT-backed viewer (CSV with room) */
function CrdtDataGridViewer({ fileId, fileName, room, onDirtyChange, onSaveRef, onDiscardRef, onConnectedChange, onReloadingChange, onGaveUpChange, selectionRef, isActive }: {
  fileId: string; fileName?: string; room: string
  onDirtyChange?: (dirty: boolean) => void
  onSaveRef?: React.MutableRefObject<(() => void) | null>
  onDiscardRef?: React.MutableRefObject<(() => void) | null>
  onConnectedChange?: (connected: boolean) => void
  onReloadingChange?: (reloading: boolean) => void
  onGaveUpChange?: (gaveUp: boolean) => void
  selectionRef?: React.MutableRefObject<{ text: string; rowCount: number } | null>
  isActive?: boolean
}) {
  const { columns, rows, loading, error, connected, isDirty, save, discard, forbidden, fileState, reloading, gaveUp } = useCrdtData(room, fileId, isActive)

  useEffect(() => {
    onConnectedChange?.(connected)
  }, [connected, onConnectedChange])

  useEffect(() => {
    onReloadingChange?.(reloading)
  }, [reloading, onReloadingChange])

  useEffect(() => {
    onGaveUpChange?.(gaveUp)
  }, [gaveUp, onGaveUpChange])

  // Relay dirty state to parent
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  // Expose save/discard functions to parent
  useEffect(() => {
    if (onSaveRef) onSaveRef.current = save
    return () => { if (onSaveRef) onSaveRef.current = null }
  }, [save, onSaveRef])

  useEffect(() => {
    if (onDiscardRef) onDiscardRef.current = discard
    return () => { if (onDiscardRef) onDiscardRef.current = null }
  }, [discard, onDiscardRef])

  if (forbidden) return <ForbiddenOverlay />

  const inner = <DataGridViewerInner fileId={fileId} fileName={fileName} columns={columns} rows={rows} loading={loading} error={error} selectionRef={selectionRef} isActive={isActive} />
  if (!fileState) return inner
  return (
    <div className="flex-1 flex flex-col overflow-hidden" style={{ minHeight: 0 }}>
      <FileStateBanner state={fileState} />
      {inner}
    </div>
  )
}

/** HTTP-fetch viewer (Parquet, or CSV without room) */
function FetchDataGridViewer({ fileId, fileName, selectionRef, isActive }: { fileId: string; fileName?: string; selectionRef?: React.MutableRefObject<{ text: string; rowCount: number } | null>; isActive?: boolean }) {
  // Inner extension: a `.shared`/`.chain` pointer streams the source bytes,
  // so `data.parquet.shared` must parse as parquet, not as CSV text.
  const ext = useMemo(() => stripPointerSuffix(fileName || fileId).split('.').pop()?.toLowerCase() || '', [fileName, fileId])
  const { columns, rows, loading, error } = useFetchData(fileId, ext)
  return <DataGridViewerInner fileId={fileId} fileName={fileName} columns={columns} rows={rows} loading={loading} error={error} selectionRef={selectionRef} isActive={isActive} />
}

/** Public component — routes to CRDT or HTTP-fetch based on room + extension */
export function DataGridViewer({ fileId, fileName, room, onDirtyChange, onSaveRef, onDiscardRef, onConnectedChange, onReloadingChange, onGaveUpChange, selectionRef, isActive }: DataGridViewerProps) {
  // Inner extension (see FetchDataGridViewer): a parquet-target pointer must
  // NOT take the CRDT branch — parquet is a one-shot binary fetch.
  const ext = useMemo(() => stripPointerSuffix(fileName || fileId).split('.').pop()?.toLowerCase() || '', [fileName, fileId])
  const usesCrdt = !!room && ext !== 'parquet'

  if (usesCrdt) {
    return <CrdtDataGridViewer fileId={fileId} fileName={fileName} room={room} onDirtyChange={onDirtyChange} onSaveRef={onSaveRef} onDiscardRef={onDiscardRef} onConnectedChange={onConnectedChange} onReloadingChange={onReloadingChange} onGaveUpChange={onGaveUpChange} selectionRef={selectionRef} isActive={isActive} />
  }
  return <FetchDataGridViewer fileId={fileId} fileName={fileName} selectionRef={selectionRef} isActive={isActive} />
}

interface DataGridViewerInnerProps {
  fileId: string
  fileName?: string
  columns: string[]
  rows: Record<string, unknown>[]
  loading: boolean
  error: string | null
  selectionRef?: React.MutableRefObject<{ text: string; rowCount: number } | null>
  isActive?: boolean
  /** A VERSION PREVIEW of the file (#929): its own command scope, parented on the workspace — see `scopeIdFor`. */
  previewVersionId?: string
}

export function DataGridViewerInner({ fileId, columns: initColumns, rows: initRows, loading, error, selectionRef, isActive, previewVersionId }: DataGridViewerInnerProps) {
  const [rowData, setRowData] = useState<Record<string, unknown>[]>([])
  const [columnNames, setColumnNames] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const [matchCount, setMatchCount] = useState(0)
  const [displayedRowCount, setDisplayedRowCount] = useState(0)
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null)
  const [stats, setStats] = useState<ColumnStats | null>(null)
  const [frozenColumns, setFrozenColumns] = useState<Set<string>>(new Set())
  const gridRef = useRef<AgGridReact>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  // Refs for search state — read by stable cell renderer without causing re-renders
  const searchStateRef = useRef({ query: '', matchSet: new Set<string>(), activeKey: '' })
  const selectedColumnRef = useRef<string | null>(null)

  // Drag-select state: track start position to distinguish text select (horizontal) vs row select (vertical)
  const dragRef = useRef<{ startIdx: number; startX: number; startY: number; active: boolean } | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Sync incoming data from CRDT or fetch
  // Only reset column order when the column set actually changes (not on row-only updates)
  const prevColumnsRef = useRef<string[]>([])
  useEffect(() => {
    if (initRows.length === 0 && initColumns.length === 0) return
    const rows = initRows.map((r, i) => (r.__rowIdx != null ? r : { ...r, __rowIdx: i }))
    const prev = prevColumnsRef.current
    const columnsChanged = prev.length !== initColumns.length || initColumns.some((c, i) => c !== prev[i])
    if (columnsChanged) {
      prevColumnsRef.current = initColumns
      setColumnNames(initColumns)
    }
    setRowData(rows)
    setDisplayedRowCount(rows.length)
  }, [initColumns, initRows])

  // Recompute stats when selection or selected column changes
  const recomputeStats = useCallback((colName: string | null) => {
    const api = gridRef.current?.api
    if (!api || !colName) {
      setStats(null)
      return
    }

    const selectedRows = api.getSelectedRows()
    let values: unknown[]

    if (selectedRows.length > 0) {
      // Stats for selected rows only
      values = selectedRows.map(row => row[colName])
    } else {
      // Stats for all visible rows
      values = [] as unknown[]
      api.forEachNodeAfterFilter(node => {
        values.push(node.data?.[colName])
      })
    }

    setStats(computeStats(values, colName))
  }, [])

  // Column select for stats
  const onColumnSelect = useCallback((colId: string) => {
    const prev = selectedColumnRef.current
    const next = prev === colId ? null : colId
    selectedColumnRef.current = next
    setSelectedColumn(next)
    // Targeted cell refresh for column highlight
    const columns = [prev, next].filter(Boolean) as string[]
    if (columns.length > 0) {
      gridRef.current?.api?.refreshCells({ force: true, columns })
    }
    recomputeStats(next)
  }, [recomputeStats])

  // Row selection changed → recompute stats + update selection ref for chat context
  const onSelectionChanged = useCallback((_e: SelectionChangedEvent) => {
    recomputeStats(selectedColumn)
    if (selectionRef) {
      const api = gridRef.current?.api
      if (api) {
        const selectedRows = api.getSelectedRows()
        if (selectedRows.length > 0) {
          const text = selectedRows.map(row =>
            columnNames.map(col => row[col] != null ? String(row[col]) : '').join('\t')
          ).join('\n').substring(0, 10000)
          selectionRef.current = { text, rowCount: selectedRows.length }
        } else {
          selectionRef.current = null
        }
      }
      window.dispatchEvent(new Event('papan:editor-selection-change'))
    }
  }, [selectedColumn, recomputeStats, selectionRef, columnNames])

  // Compute matches and update ref + trigger cell repaint (no colDef change)
  const matchesRef = useRef<SearchMatch[]>([])

  const recomputeMatches = useCallback((query: string, activeIdx: number) => {
    const matches: SearchMatch[] = []
    const matchSet = new Set<string>()

    if (query) {
      const q = query.toLowerCase()
      const api = gridRef.current?.api

      if (api) {
        api.forEachNodeAfterFilterAndSort(node => {
          const rowIdx = node.data?.__rowIdx as number
          if (rowIdx == null) return
          for (const col of columnNames) {
            const val = String(node.data[col] ?? '')
            if (val.toLowerCase().includes(q)) {
              matches.push({ rowIdx, colId: col })
              matchSet.add(`${rowIdx}:${col}`)
            }
          }
        })
      } else {
        for (let i = 0; i < rowData.length; i++) {
          const row = rowData[i]
          for (const col of columnNames) {
            const val = String(row[col] ?? '')
            if (val.toLowerCase().includes(q)) {
              matches.push({ rowIdx: i, colId: col })
              matchSet.add(`${i}:${col}`)
            }
          }
        }
      }
    }

    matchesRef.current = matches
    const safeIdx = matches.length > 0 ? ((activeIdx % matches.length) + matches.length) % matches.length : 0
    const activeMatch = matches[safeIdx]
    const activeKey = activeMatch ? `${activeMatch.rowIdx}:${activeMatch.colId}` : ''

    searchStateRef.current = { query, matchSet, activeKey }
    setMatchCount(matches.length)
    setCurrentMatchIndex(safeIdx)

    gridRef.current?.api?.refreshCells({ force: true })

    return { matches, safeIdx }
  }, [rowData, columnNames])

  // When search query changes, recompute and jump to first match
  useEffect(() => {
    const { matches, safeIdx } = recomputeMatches(searchQuery, 0)
    if (matches.length > 0) jumpToMatch(matches, safeIdx)
  }, [searchQuery]) // eslint-disable-line react-hooks/exhaustive-deps

  // Jump to a specific match by index
  const jumpToMatch = useCallback((matches: SearchMatch[], idx: number) => {
    const api = gridRef.current?.api
    if (!api || matches.length === 0) return
    const match = matches[idx]

    const rowCount = api.getDisplayedRowCount()
    for (let i = 0; i < rowCount; i++) {
      const node = api.getDisplayedRowAtIndex(i)
      if (node?.data?.__rowIdx === match.rowIdx) {
        api.ensureIndexVisible(i, 'middle')
        api.ensureColumnVisible(match.colId)
        return
      }
    }

    const pageSize = api.paginationGetPageSize()
    if (pageSize > 0) {
      let displayIdx = -1
      api.forEachNodeAfterFilterAndSort((node, i) => {
        if (node.data?.__rowIdx === match.rowIdx) displayIdx = i
      })
      if (displayIdx >= 0) {
        api.paginationGoToPage(Math.floor(displayIdx / pageSize))
        requestAnimationFrame(() => {
          const count = api.getDisplayedRowCount()
          for (let i = 0; i < count; i++) {
            const node = api.getDisplayedRowAtIndex(i)
            if (node?.data?.__rowIdx === match.rowIdx) {
              api.ensureIndexVisible(i, 'middle')
              api.ensureColumnVisible(match.colId)
              return
            }
          }
        })
      }
    }
  }, [])

  const goNext = useCallback(() => {
    const matches = matchesRef.current
    if (matches.length === 0) return
    const nextIdx = (currentMatchIndex + 1) % matches.length
    const { safeIdx } = recomputeMatches(searchQuery, nextIdx)
    jumpToMatch(matches, safeIdx)
  }, [currentMatchIndex, searchQuery, recomputeMatches, jumpToMatch])

  const goPrev = useCallback(() => {
    const matches = matchesRef.current
    if (matches.length === 0) return
    const prevIdx = (currentMatchIndex - 1 + matches.length) % matches.length
    const { safeIdx } = recomputeMatches(searchQuery, prevIdx)
    jumpToMatch(matches, safeIdx)
  }, [currentMatchIndex, searchQuery, recomputeMatches, jumpToMatch])

  // End drag-select on mouseup anywhere
  useEffect(() => {
    const onMouseUp = () => { dragRef.current = null; setIsDragging(false) }
    window.addEventListener('mouseup', onMouseUp)
    return () => window.removeEventListener('mouseup', onMouseUp)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        setShowSearch(true)
        requestAnimationFrame(() => searchRef.current?.focus())
      }
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false)
        setSearchQuery('')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [showSearch])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (e.shiftKey) goPrev()
      else goNext()
    }
  }, [goNext, goPrev])

  // Stable cell renderer — reads from ref, never causes colDef rebuild
  const cellRenderer = useCallback((params: ICellRendererParams) => {
    const value = String(params.value ?? '')
    const { query, matchSet, activeKey } = searchStateRef.current
    if (!query || !params.colDef?.field) return value

    const key = `${params.data?.__rowIdx}:${params.colDef.field}`
    if (!matchSet.has(key)) return value

    const isActive = key === activeKey
    const q = query.toLowerCase()
    const parts: { text: string; highlight: boolean }[] = []
    let remaining = value
    let lower = remaining.toLowerCase()
    let idx = lower.indexOf(q)

    while (idx !== -1) {
      if (idx > 0) parts.push({ text: remaining.slice(0, idx), highlight: false })
      parts.push({ text: remaining.slice(idx, idx + q.length), highlight: true })
      remaining = remaining.slice(idx + q.length)
      lower = remaining.toLowerCase()
      idx = lower.indexOf(q)
    }
    if (remaining) parts.push({ text: remaining, highlight: false })

    return (
      <span>
        {parts.map((part, i) =>
          part.highlight ? (
            <mark
              key={i}
              style={{
                backgroundColor: isActive ? '#f59e0b' : '#854d0e',
                color: isActive ? '#000' : '#fef3c7',
                borderRadius: 2,
                padding: '0 1px',
              }}
            >
              {part.text}
            </mark>
          ) : (
            <span key={i}>{part.text}</span>
          )
        )}
      </span>
    )
  }, []) // Stable — reads from searchStateRef

  // Stable cellStyle — reads from ref, highlights selected column
  const cellStyle = useCallback((params: { colDef?: { field?: string } }) => {
    if (selectedColumnRef.current && params.colDef?.field === selectedColumnRef.current) {
      return { backgroundColor: 'rgba(59, 130, 246, 0.08)' }
    }
    return { backgroundColor: '' }
  }, [])

  const toggleFreezeColumn = useCallback((colId: string) => {
    setFrozenColumns(prev => {
      const next = new Set(prev)
      if (next.has(colId)) next.delete(colId)
      else next.add(colId)
      return next
    })
  }, [])

  const moveColumnToBack = useCallback((colId: string) => {
    // Unfreeze if frozen
    setFrozenColumns(prev => {
      if (!prev.has(colId)) return prev
      const next = new Set(prev)
      next.delete(colId)
      return next
    })
    // Move to end in column order
    setColumnNames(prev => {
      const idx = prev.indexOf(colId)
      if (idx < 0 || idx === prev.length - 1) return prev
      const next = [...prev]
      next.splice(idx, 1)
      next.push(colId)
      return next
    })
  }, [])

  // --- Context menus ---
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; value: string; colId: string } | null>(null)
  const [headerCtxMenu, setHeaderCtxMenu] = useState<{ x: number; y: number; colId: string } | null>(null)
  const ctxMenuRef = useRef<HTMLDivElement>(null)
  const headerCtxMenuRef = useRef<HTMLDivElement>(null)
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  // Close any context menu on outside click / Escape
  const activeCtx = ctxMenu || headerCtxMenu
  useEffect(() => {
    if (!activeCtx) return
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (ctxMenuRef.current?.contains(t) || headerCtxMenuRef.current?.contains(t)) return
      setCtxMenu(null)
      setHeaderCtxMenu(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setCtxMenu(null); setHeaderCtxMenu(null) } }
    window.addEventListener('mousedown', onMouseDown, true)
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('mousedown', onMouseDown, true); window.removeEventListener('keydown', onKey) }
  }, [activeCtx])

  // Viewport clamp — cell context menu
  useEffect(() => {
    if (!ctxMenu || !ctxMenuRef.current) return
    const el = ctxMenuRef.current
    const rect = el.getBoundingClientRect()
    let { x, y } = ctxMenu
    if (x + rect.width > window.innerWidth - 4) x = window.innerWidth - rect.width - 4
    if (y + rect.height > window.innerHeight - 4) y = window.innerHeight - rect.height - 4
    el.style.left = `${x}px`
    el.style.top = `${y}px`
  }, [ctxMenu])

  // Viewport clamp — header context menu
  useEffect(() => {
    if (!headerCtxMenu || !headerCtxMenuRef.current) return
    const el = headerCtxMenuRef.current
    const rect = el.getBoundingClientRect()
    let { x, y } = headerCtxMenu
    if (x + rect.width > window.innerWidth - 4) x = window.innerWidth - rect.width - 4
    if (y + rect.height > window.innerHeight - 4) y = window.innerHeight - rect.height - 4
    el.style.left = `${x}px`
    el.style.top = `${y}px`
  }, [headerCtxMenu])

  const onHeaderContextMenu = useCallback((colId: string, x: number, y: number) => {
    setCtxMenu(null)
    setHeaderCtxMenu({ x, y, colId })
  }, [])

  // --- Copy helpers ---
  const columnNamesRef = useRef(columnNames)
  columnNamesRef.current = columnNames

  const copyRows = useCallback(() => {
    const api = gridRef.current?.api
    if (!api) return
    const selectedRows = api.getSelectedRows()
    if (selectedRows.length === 0) return
    const cols = columnNamesRef.current
    const header = cols.join('\t')
    const body = selectedRows.map(row =>
      cols.map(col => String(row[col] ?? '')).join('\t')
    ).join('\n')
    navigator.clipboard.writeText(`${header}\n${body}`)
  }, [])

  const copyCells = useCallback((colOverride?: string) => {
    const api = gridRef.current?.api
    if (!api) return
    const col = colOverride || selectedColumnRef.current
    if (!col) return
    const selectedRows = api.getSelectedRows()
    if (selectedRows.length === 0) return
    const values = selectedRows.map(row => String(row[col] ?? ''))
    navigator.clipboard.writeText(values.join('\n'))
  }, [])

  const copyColumn = useCallback((colOverride?: string) => {
    const api = gridRef.current?.api
    if (!api) return
    const col = colOverride || selectedColumnRef.current
    if (!col) return
    const values: string[] = []
    api.forEachNodeAfterFilter(node => {
      values.push(String(node.data?.[col] ?? ''))
    })
    navigator.clipboard.writeText(values.join('\n'))
  }, [])

  // --- Command scope registration ---
  const cmdRegistry = useCommandRegistryOptional()
  const copyRowsRef = useRef(copyRows)
  copyRowsRef.current = copyRows
  const copyCellsRef = useRef(copyCells)
  copyCellsRef.current = copyCells
  const copyColumnRef = useRef(copyColumn)
  copyColumnRef.current = copyColumn

  // A version preview is its own scope, parented on the workspace: it shares the live grid's file id (one id let
  // the first to unmount delete the other's commands), and under 'editor' its read-only Copy commands were greyed by
  // the cover its own overlay puts on the live editor (#929).
  const gridScopeId = previewVersionId !== undefined ? `editor:datagrid:${fileId}@${previewVersionId}` : `editor:datagrid:${fileId}`
  const gridParentScopeId = previewVersionId !== undefined ? 'workspace' : 'editor'
  useEffect(() => {
    if (!cmdRegistry) return
    return cmdRegistry.registerScope({
      scopeId: gridScopeId,
      parentScopeId: gridParentScopeId,
      get commands() {
        const api = gridRef.current?.api
        const selectedRows = api?.getSelectedRows() || []
        const hasRows = selectedRows.length > 0
        const col = selectedColumnRef.current
        return [
          // Shadow parent's standard edit commands
          { id: 'edit.copy', label: 'Copy', hidden: true, execute: () => {}, enabled: false, menuGroup: 'edit' as const, menuOrder: 10 },
          { id: 'edit.cut', label: 'Cut', hidden: true, execute: () => {}, enabled: false, menuGroup: 'edit' as const, menuOrder: 11 },
          { id: 'edit.paste', label: 'Paste', hidden: true, execute: () => {}, enabled: false, menuGroup: 'edit' as const, menuOrder: 12 },
          { id: 'edit.delete', label: 'Delete', hidden: true, execute: () => {}, enabled: false, menuGroup: 'edit' as const, menuOrder: 50 },
          // View-mode commands
          {
            id: 'edit.copy-rows',
            label: hasRows ? `Copy ${selectedRows.length} Row${selectedRows.length > 1 ? 's' : ''}` : 'Copy Rows',
            execute: () => copyRowsRef.current(),
            enabled: hasRows,
            menuGroup: 'edit' as const,
            menuOrder: 10,
          },
          {
            id: 'edit.copy-cells',
            label: 'Copy Cells',
            execute: () => copyCellsRef.current(),
            enabled: hasRows && !!col,
            menuGroup: 'edit' as const,
            menuOrder: 11,
          },
          {
            id: 'edit.copy-column',
            label: col ? `Copy Column "${col}"` : 'Copy Column',
            execute: () => copyColumnRef.current(),
            enabled: !!col,
            menuGroup: 'edit' as const,
            menuOrder: 12,
          },
        ]
      },
    })
  }, [cmdRegistry, gridScopeId, gridParentScopeId])

  // Focus scope: set active scope on mousedown and when isActive
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null)
  const gridCallbackRef = useCallback((el: HTMLDivElement | null) => setGridEl(el), [])

  useEffect(() => {
    if (isActive && cmdRegistry) {
      const raf = requestAnimationFrame(() => cmdRegistry.setActiveScope(gridScopeId))
      return () => cancelAnimationFrame(raf)
    }
  }, [isActive, gridScopeId, cmdRegistry])

  useEffect(() => {
    if (!gridEl || !cmdRegistry) return
    const handler = () => cmdRegistry.setActiveScope(gridScopeId)
    gridEl.addEventListener('mousedown', handler, true)
    return () => gridEl.removeEventListener('mousedown', handler, true)
  }, [gridEl, gridScopeId, cmdRegistry])

  // Column defs — only changes when columns change, NOT on search
  const colDefs = useMemo<ColDef[]>(() => {
    return columnNames.map(col => ({
      field: col,
      headerName: col,
      filter: true,
      sortable: false,
      resizable: true,
      cellRenderer,
      cellStyle,
      pinned: frozenColumns.has(col) ? ('left' as const) : null,
      headerComponent: CustomHeader,
      headerComponentParams: { onColumnSelect, selectedColumn, onHeaderContextMenu },
    }))
  }, [columnNames, cellRenderer, cellStyle, onColumnSelect, frozenColumns, selectedColumn, onHeaderContextMenu])

  const updateDisplayedCount = useCallback(() => {
    const api = gridRef.current?.api
    if (!api) return
    let count = 0
    api.forEachNodeAfterFilter(() => { count++ })
    setDisplayedRowCount(count)
  }, [])

  const onGridReady = useCallback(() => {
    const api = gridRef.current?.api
    if (api) {
      const minWidth = 120
      const gridWidth = (gridRef.current as any)?.eGui?.clientWidth ?? window.innerWidth
      const totalMinWidth = columnNames.length * minWidth
      if (totalMinWidth <= gridWidth) {
        api.sizeColumnsToFit()
      } else {
        api.setColumnWidths(columnNames.map(col => ({ key: col, newWidth: minWidth })))
      }
    }
    updateDisplayedCount()
  }, [updateDisplayedCount, columnNames])

  const onSortChanged = useCallback(() => {
    if (searchQuery) recomputeMatches(searchQuery, 0)
  }, [searchQuery, recomputeMatches])

  const onFilterChanged = useCallback(() => {
    updateDisplayedCount()
    if (searchQuery) recomputeMatches(searchQuery, 0)
    recomputeStats(selectedColumn)
  }, [searchQuery, recomputeMatches, updateDisplayedCount, recomputeStats, selectedColumn])

  // Only show the loading spinner on the FIRST load (no columns yet). On a
  // reconnect rebuild the doc resyncs (loading flips true again) but the prior
  // columns/rows are retained (the data effect skips while !synced), so keep the
  // grid painted instead of blanking — the "Reconnecting…" badge signals the
  // refresh. Mirrors the text editors' useCollabText freeze.
  if (loading && initColumns.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin themed-text-muted" size={24} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <p>Failed to load file: {error}</p>
      </div>
    )
  }

  return (
    <div ref={gridCallbackRef} className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="h-10 border-b themed-border flex items-center px-4 gap-2 shrink-0 themed-bg-secondary">
        {showSearch ? (
          <div className="flex items-center gap-1.5 flex-1">
            <Search size={14} className="themed-text-muted shrink-0" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Find in cells..."
              className="flex-1 bg-transparent text-sm themed-text focus:outline-none min-w-0"
              autoFocus
            />
            {searchQuery && (
              <span className="text-xs themed-text-muted whitespace-nowrap">
                {matchCount > 0
                  ? `${currentMatchIndex + 1} of ${matchCount}`
                  : 'No matches'}
              </span>
            )}
            <button
              onClick={goPrev}
              disabled={matchCount === 0}
              className="themed-text-muted hover:themed-text disabled:opacity-30 p-0.5"
              title={`Previous (${formatShortcut({ key: 'Enter', shift: true })})`}
            >
              <ChevronUp size={14} />
            </button>
            <button
              onClick={goNext}
              disabled={matchCount === 0}
              className="themed-text-muted hover:themed-text disabled:opacity-30 p-0.5"
              title={`Next (${formatShortcut({ key: 'Enter' })})`}
            >
              <ChevronDown size={14} />
            </button>
            <button
              onClick={() => { setShowSearch(false); setSearchQuery('') }}
              className="themed-text-muted hover:themed-text p-0.5"
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => { setShowSearch(true); requestAnimationFrame(() => searchRef.current?.focus()) }}
            className="flex items-center gap-1.5 px-2 py-1 text-sm rounded themed-text-muted hover:themed-bg-tertiary hover:themed-text transition-colors flex-1 min-w-0"
            title={`Find (${formatShortcut({ key: 'f', mod: true })})`}
          >
            <Search size={14} className="shrink-0" />
            <span className="truncate">Find in cells...</span>
          </button>
        )}

        <span className="text-xs themed-text-muted">
          {displayedRowCount < rowData.length
            ? `${displayedRowCount.toLocaleString()} of ${rowData.length.toLocaleString()} rows`
            : `${rowData.length.toLocaleString()} rows`}
        </span>
      </div>

      {/* AG Grid */}
      <div className={`flex-1 min-h-0${isDragging ? ' select-none' : ''}`}>
        <AgGridReact
          ref={gridRef}
          theme={darkTheme}
          rowData={rowData}
          columnDefs={colDefs}
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          onSelectionChanged={onSelectionChanged}
          animateRows={false}
          suppressCellFocus={true}
          enableCellTextSelection={true}
          preventDefaultOnContextMenu
          onCellContextMenu={(e) => {
            const me = e.event as MouseEvent
            me.preventDefault()
            const value = String(e.value ?? '')
            const colId = e.column?.getColId() || ''
            setHeaderCtxMenu(null)
            setCtxMenu({ x: me.clientX, y: me.clientY, value, colId })
          }}
          rowSelection={{ mode: 'multiRow', checkboxes: false, headerCheckbox: false }}
          onCellMouseDown={(e) => {
            if (!e.node || e.node.rowIndex == null) return
            const me = e.event as MouseEvent
            if (me?.ctrlKey || me?.metaKey || me?.shiftKey) return
            dragRef.current = { startIdx: e.node.rowIndex, startX: me.clientX, startY: me.clientY, active: false }
          }}
          onCellMouseOver={(e) => {
            const drag = dragRef.current
            if (!drag || !e.node || e.node.rowIndex == null) return
            const me = e.event as MouseEvent
            const curIdx = e.node.rowIndex
            if (!drag.active && curIdx !== drag.startIdx) {
              // Only activate row drag if vertical movement > horizontal and exceeds threshold
              const dx = Math.abs(me.clientX - drag.startX)
              const dy = Math.abs(me.clientY - drag.startY)
              if (dy < 8 || dy <= dx) {
                // Horizontal drag — let browser handle text selection
                dragRef.current = null
                return
              }
              drag.active = true
              setIsDragging(true)
              window.getSelection()?.removeAllRanges()
            }
            if (drag.active) {
              const [from, to] = drag.startIdx < curIdx
                ? [drag.startIdx, curIdx]
                : [curIdx, drag.startIdx]
              e.api.deselectAll()
              for (let i = from; i <= to; i++) {
                e.api.getDisplayedRowAtIndex(i)?.setSelected(true)
              }
            }
          }}
          onRowClicked={(e) => {
            // Skip if drag-select just happened
            if (dragRef.current?.active) return
            dragRef.current = null
            if (!e.node) return
            const me = e.event as MouseEvent
            if (me?.ctrlKey || me?.metaKey) {
              e.node.setSelected(!e.node.isSelected())
            } else if (me?.shiftKey) {
              const selected = e.api.getSelectedNodes()
              if (selected.length > 0) {
                const lastIdx = selected[selected.length - 1].rowIndex ?? 0
                const curIdx = e.node.rowIndex ?? 0
                const [from, to] = lastIdx < curIdx ? [lastIdx, curIdx] : [curIdx, lastIdx]
                for (let i = from; i <= to; i++) {
                  e.api.getDisplayedRowAtIndex(i)?.setSelected(true)
                }
              } else {
                e.node.setSelected(true)
              }
            } else {
              const selected = e.api.getSelectedNodes()
              if (selected.length === 1 && selected[0].id === e.node.id) {
                e.node.setSelected(false)
              } else {
                e.api.deselectAll()
                e.node.setSelected(true)
              }
            }
          }}
          pagination={rowData.length > 1000}
          paginationPageSize={1000}
          paginationPageSizeSelector={[100, 500, 1000, 5000]}
        />
      </div>

      {/* Status bar — shows stats when a column header is clicked */}
      {stats && <StatsBar
        stats={stats}
        frozenColumns={frozenColumns}
        onToggleFreeze={toggleFreezeColumn}
        onMoveToBack={moveColumnToBack}
        onDismiss={() => { const prev = selectedColumnRef.current; setSelectedColumn(null); setStats(null); selectedColumnRef.current = null; if (prev) gridRef.current?.api?.refreshCells({ force: true, columns: [prev] }) }}
      />}

      {/* Right-click context menu */}
      {ctxMenu && createPortal(
        <div
          ref={ctxMenuRef}
          className={`fixed themed-bg-tertiary border themed-border shadow-xl py-1 z-50 min-w-[160px] ${themeClass}`}
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
        >
          <button
            onClick={() => { navigator.clipboard.writeText(ctxMenu.value); setCtxMenu(null) }}
            className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
          >
            <Copy size={14} />
            Copy Value
          </button>
          {(() => {
            const api = gridRef.current?.api
            const selectedRows = api?.getSelectedRows() || []
            return selectedRows.length > 0 && (
              <button
                onClick={() => { copyRows(); setCtxMenu(null) }}
                className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
              >
                <Copy size={14} />
                Copy {selectedRows.length} Row{selectedRows.length > 1 ? 's' : ''}
              </button>
            )
          })()}
          {(() => {
            const api2 = gridRef.current?.api
            const hasRowSel = (api2?.getSelectedRows()?.length ?? 0) > 0
            return hasRowSel && ctxMenu.colId && (
              <button
                onClick={() => { copyCells(ctxMenu.colId); setCtxMenu(null) }}
                className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
              >
                <Copy size={14} />
                Copy Cells
              </button>
            )
          })()}
          {ctxMenu.colId && (
            <button
              onClick={() => { copyColumn(ctxMenu.colId); setCtxMenu(null) }}
              className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
            >
              <Copy size={14} />
              Copy Column
            </button>
          )}
        </div>,
        document.body,
      )}

      {/* Header right-click context menu */}
      {headerCtxMenu && createPortal(
        <div
          ref={headerCtxMenuRef}
          className={`fixed themed-bg-tertiary border themed-border shadow-xl py-1 z-50 min-w-[160px] ${themeClass}`}
          style={{ left: headerCtxMenu.x, top: headerCtxMenu.y }}
        >
          <button
            onClick={() => {
              toggleFreezeColumn(headerCtxMenu.colId)
              setHeaderCtxMenu(null)
            }}
            className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
          >
            {frozenColumns.has(headerCtxMenu.colId) ? <Unlock size={14} /> : <Lock size={14} />}
            {frozenColumns.has(headerCtxMenu.colId) ? 'Unfreeze Column' : 'Freeze Column'}
          </button>
          <button
            onClick={() => {
              moveColumnToBack(headerCtxMenu.colId)
              setHeaderCtxMenu(null)
            }}
            className="w-full px-3 py-1.5 text-left text-sm themed-text-secondary hover:themed-text hover:themed-bg-secondary flex items-center gap-2"
          >
            <ArrowRightToLine size={14} />
            Move to Back
          </button>
        </div>,
        document.body,
      )}
    </div>
  )
}
